// The seat of a user on a bout (#664, F27): jury, ring referee or side judge.
//
// This file is the ONE place in the frontend that tests which seat the user
// holds on a bout. A component asks boutSeatsOf() and reads the answer:
//   seats.jury         the user is the jury of the bout (an admin is not).
//   seats.ringReferee  the user is the ring referee of the bout.
//   seats.sideJudge    the user is one of the side judges of the bout.
//   seats.any          the user holds one of the three seats.
// The server decides every action. A seat only shows or hides a control.
//
// Two row shapes carry the officials:
//   the bout page      `referees[].id` and `jury_id`
//   the bout list      `side_judges[].user_id`, and no `jury_id`: the jury of
//                      a list row comes from the jury assignments of the
//                      tournament, so the list passes `juryId`.
// A guest has no id. A guest holds no seat, also for a bout that has no
// official yet (undefined must never match undefined).
//
// A row of a list that is compared with the user is not a seat test. The mark
// "this row is me" in BoutOfficials (`ref.id === user?.id`) says which official
// of a list is the user. It does not say which seat the user holds on the bout.
// It stays in its component.
//
// e2e/tests/bout-seats-664.spec.ts fails on a seat test in any other file.

/** The fields of a bout row that name its officials. */
export interface BoutSeatRow {
  jury_id?: string | null;
  ring_referee_id?: string | null;
  referees?: ReadonlyArray<{ id: string }> | null;
  side_judges?: ReadonlyArray<{ user_id: string }> | null;
}

export interface BoutSeats {
  jury: boolean;
  ringReferee: boolean;
  sideJudge: boolean;
  any: boolean;
}

/** The seats of `userId` on `bout`. `juryId` is the jury of the bout when the
 *  row has no `jury_id` (a list row). */
export function boutSeatsOf(
  bout: BoutSeatRow,
  userId: string | null | undefined,
  juryId: string | null | undefined = bout.jury_id,
): BoutSeats {
  if (!userId) return { jury: false, ringReferee: false, sideJudge: false, any: false };
  const jury = userId === juryId;
  const ringReferee = userId === bout.ring_referee_id;
  const sideJudge = !!bout.referees?.some(r => r.id === userId)
    || !!bout.side_judges?.some(r => r.user_id === userId);
  return { jury, ringReferee, sideJudge, any: jury || ringReferee || sideJudge };
}
